// The planning clock: real minutes, counting down, in the top bar of stage 2. It is the only clock in
// the game that runs in real time rather than sim time, and it is the point of the stage — a plan you
// cannot finish in the time you have is the lesson (spec §2 stage 2).
//
// At zero the fight starts itself, with whatever plan exists.

import { useEffect, useRef, useState } from 'react';

/** Under a minute the clock turns amber and pulses once a second. */
const WARN_MS = 60_000;

export function msLeft(deadline: number | null, now = Date.now()): number {
  if (deadline === null) return 0;
  return Math.max(0, deadline - now);
}

export function mmss(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function PlanClock({ deadline, onExpire }: { deadline: number | null; onExpire: () => void }) {
  const [left, setLeft] = useState(() => msLeft(deadline));
  const fired = useRef(false);

  // The expiry handler is read through a ref, not captured by the interval's closure.
  //
  // It used to be captured, on the grounds that it was always the store's own `executePlan`, whose
  // identity is stable. It no longer is: the planning stage's handler has to see the task picker as it
  // stands at zero (it commits the pending task rather than discarding it), and a closure captured on
  // the first render would have seen the picker as it was when the clock started — which is to say,
  // closed, every time.
  const expire = useRef(onExpire);
  expire.current = onExpire;

  useEffect(() => {
    fired.current = false;
    setLeft(msLeft(deadline));
    if (deadline === null) return;
    const id = setInterval(() => {
      const ms = msLeft(deadline);
      setLeft(ms);
      if (ms <= 0 && !fired.current) {
        fired.current = true;
        clearInterval(id);
        expire.current();
      }
    }, 250);
    return () => clearInterval(id);
  }, [deadline]);

  const warn = left <= WARN_MS;
  return (
    <span
      data-testid="plan-clock"
      data-warn={warn ? 'true' : 'false'}
      title="Planning time remaining. At zero the fight starts with the plan as it stands."
      // The last minute pulses once a second, on opacity only. Nothing in this interface moves to get
      // your attention (spec §8, "nothing bounces") — but a clock that is about to take the decision
      // out of your hands has earned a heartbeat.
      className={`mono text-[18px] tracking-[0.1em] ${warn ? 'wt-pulse' : ''}`}
      style={{ color: warn ? 'var(--color-warn)' : 'var(--color-paper)' }}
    >
      {mmss(left)}
    </span>
  );
}
